import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSessionQuery } from "../../features/auth/hooks";
import { clientManagementApi } from "../../features/admin/settings/api/clientManagement";
import type { ClientSummary } from "../../features/admin/settings/api/clientManagement";
import { qk } from "../../lib/queryKeys";

const STORAGE_KEY = "peak4_selected_client_id";

interface ClientScopeContextValue {
  isCentralAdmin: boolean;
  clients: ClientSummary[];
  selectedClientId: string;
  setSelectedClientId: (clientId: string) => void;
  effectiveClientId: string | null;
  /** Whether the currently active client has anonymisation enabled. Always false for central admins. */
  isAnonymised: boolean;
}

const ClientScopeContext = createContext<ClientScopeContextValue | null>(null);

export const ClientScopeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data: session } = useSessionQuery();
  const isCentralAdmin = session?.user?.is_central_admin ?? false;
  const userClientId = session?.user?.client_id ?? null;
  const [selectedClientId, setSelectedClientId] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(STORAGE_KEY) || "";
  });

  const clientsQuery = useQuery({
    queryKey: qk.clients(),
    queryFn: () => clientManagementApi.listClients(),
    enabled: isCentralAdmin,
    staleTime: 5 * 60 * 1000,
  });

  // Regular admins: fetch their own client's details to get enable_anonymisation.
  const regularClientQuery = useQuery({
    queryKey: qk.clientDetails(userClientId),
    queryFn: () => clientManagementApi.getClientDetails(userClientId!),
    enabled: !isCentralAdmin && !!userClientId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (selectedClientId) {
      localStorage.setItem(STORAGE_KEY, selectedClientId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [selectedClientId]);

  const value = useMemo<ClientScopeContextValue>(() => {
    const effectiveClientId = isCentralAdmin
      ? selectedClientId || null
      : userClientId;

    // Central admins can see all data — they are never subject to anonymisation blur.
    // Regular admins see their own client's anonymisation flag.
    const isAnonymised = isCentralAdmin
      ? false
      : (regularClientQuery.data?.data?.enable_anonymisation ?? false);

    return {
      isCentralAdmin,
      clients: clientsQuery.data?.data ?? [],
      selectedClientId,
      setSelectedClientId,
      effectiveClientId,
      isAnonymised,
    };
  }, [
    clientsQuery.data?.data,
    isCentralAdmin,
    regularClientQuery.data?.data,
    selectedClientId,
    userClientId,
  ]);

  return (
    <ClientScopeContext.Provider value={value}>
      {children}
    </ClientScopeContext.Provider>
  );
};

export const useClientScope = () => {
  const context = useContext(ClientScopeContext);
  if (!context) {
    throw new Error("useClientScope must be used within ClientScopeProvider");
  }
  return context;
};
